import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

/**
 * Sets `document.title` from the deepest primary route `title`, treating it as an ngx-translate key.
 *
 * Angular stores resolved titles under an internal `data` key; that entry is not always present when
 * `updateTitle` runs (e.g. certain guard/resolve ordering). We fall back to `routeConfig.title` so
 * static string titles from `Routes` still work.
 */
@Injectable()
export class TranslateTitleStrategy extends TitleStrategy {
    private readonly browserTitle = inject(Title);
    private readonly translate = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    /** Last snapshot from `updateTitle`; avoids injecting `Router` (Router → TitleStrategy → … → Router cycle). */
    private lastSnapshot: RouterStateSnapshot | null = null;

    constructor() {
        super();
        this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.lastSnapshot) {
                this.applyTitle(this.lastSnapshot);
            }
        });
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.lastSnapshot = snapshot;
        this.applyTitle(snapshot);
    }

    override getResolvedTitleForRoute(snapshot: ActivatedRouteSnapshot): unknown {
        const fromData = super.getResolvedTitleForRoute(snapshot);
        if (fromData != null && fromData !== '') {
            return fromData;
        }
        const staticTitle = snapshot.routeConfig?.title;
        return typeof staticTitle === 'string' ? staticTitle : undefined;
    }

    private applyTitle(snapshot: RouterStateSnapshot): void {
        const titleKey = this.buildTitle(snapshot);
        const resolved =
            titleKey !== undefined && titleKey !== ''
                ? this.translate.instant(titleKey)
                : this.translate.instant('APP.DEFAULT_TAB_TITLE');
        this.browserTitle.setTitle(resolved);
    }
}
